<template>
    <div class="bg-gray-200">
        <page-header>
            <template slot="title">Dashboard</template>
        </page-header>
        <spinner line-fg-color="#F02629" v-if="chartsLoading" class="w-full mt-4 flex-col"></spinner>
        <div class="p-6 bg-gray-200">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div class="mb-6">
                    <h3 class="text-lg leading-6 font-medium text-gray-900">
                        Member Types
                    </h3>
                    <dl class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
                        <div class="px-4 py-5 bg-white shadow rounded-lg overflow-hidden sm:p-6" v-for="(type, index) in chartsData.memberTypes" :key="index">
                            <dt class="text-sm font-medium text-gray-700 truncate">
                                {{type.name}}
                            </dt>
                            <dd class="mt-1 text-3xl font-semibold text-gray-900">
                                {{type.value}}<small class="pl-2 text-base text-gray-600">{{type.percentage}}%</small>
                            </dd>
                        </div>
                    </dl>
                </div>
                <div class="mb-6">
                    <h3 class="text-lg leading-6 font-medium text-gray-900">
                        Login Status <small class="text-1xs text-gray-600">(Last 12 Months)</small>
                    </h3>
                    <dl class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <div class="px-4 py-5 bg-white shadow rounded-lg overflow-hidden sm:p-6" v-for="(type, index) in chartsData.loggedMembers" :key="index">
                            <dt class="text-sm font-medium text-gray-700 truncate">
                                {{type.name}}
                            </dt>
                            <dd class="mt-1 text-3xl font-semibold text-gray-900">
                                {{type.value}}<small class="pl-2 text-base text-gray-600">{{type.percentage}}%</small>
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>
            <div class="mb-6">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                    Application Statuses <small class="text-1xs text-gray-600">(Active Forms)</small>
                </h3>
                <dl class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-6">
                    <div
                        v-for="(status, index) in chartsData.applicationStatus"
                        @click="statusCardClicked('application', status.name)"
                        class="px-4 py-5 bg-white shadow rounded-lg overflow-hidden sm:p-6 cursor-pointer"
                        :key="index"
                    >
                        <dt class="text-sm font-medium text-gray-700 truncate">
                            {{status.name}}
                        </dt>
                        <dd class="mt-1 text-3xl font-semibold text-gray-900">
                            {{status.value}}<small class="pl-2 text-base text-gray-600">{{status.percentage}}%</small>
                        </dd>
                    </div>
                </dl>
            </div>
            <div class="mb-6">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                    FAS Statuses <small class="text-1xs text-gray-600">(Active Forms)</small>
                </h3>
                <dl class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-6">
                    <div
                        v-for="(status, index) in chartsData.fasStatus"
                        @click="statusCardClicked('tap', status.name)"
                        :key="index"
                        class="px-4 py-5 bg-white shadow rounded-lg overflow-hidden sm:p-6 cursor-pointer"
                    >
                        <dt class="text-sm font-medium text-gray-700 truncate">
                            {{status.name}}
                        </dt>
                        <dd class="mt-1 text-3xl font-semibold text-gray-900">
                            {{status.value}}<small class="pl-2 text-base text-gray-600">{{status.percentage}}%</small>
                        </dd>
                    </div>
                </dl>
            </div>
            <div class="mb-6">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                    Supplier Statuses <small class="text-1xs text-gray-600">(Active Forms)</small>
                </h3>
                <dl class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-6">
                    <div
                        v-for="(status, index) in chartsData.supplierStatus"
                        @click="statusCardClicked('supplier', status.name)"
                        :key="index"
                        class="px-4 py-5 bg-white shadow rounded-lg overflow-hidden sm:p-6 cursor-pointer"
                    >
                        <dt class="text-sm font-medium text-gray-700 truncate">
                            {{status.name}}
                        </dt>
                        <dd class="mt-1 text-3xl font-semibold text-gray-900">
                            {{status.value}}<small class="pl-2 text-base text-gray-600">{{status.percentage}}%</small>
                        </dd>
                    </div>
                </dl>
            </div>
            <div class="pb-10">
                <!-- Row 1 -->
                <div class="w-full flex flex-wrap">
                    <!-- Total Users of FAS -->
                    <div class="w-full xl:w-1/3 mt-10" v-if="chartsData.totalUsers.series">
                        <highcharts :options="getStackedBarChartOptions(chartsData.totalUsers)" ></highcharts>
                    </div>
                    <!-- Total Logged in Members -->
                    <div class="w-full xl:w-1/3 mt-10" v-if="chartsData.loggedMembers.series">
                        <highcharts :options="getStackedBarChartOptions(chartsData.loggedMembers)" ></highcharts>
                    </div>
                    <!-- Application Popularity -->
                    <div class="w-full mt-10" v-if="chartsData.applicationPopularity.series">
                        <highcharts :options="getBarChartOptions(chartsData.applicationPopularity)" ></highcharts>
                    </div>
                    <!-- Submission Breakdown -->
                    <div class="w-full mt-10" v-if="chartsData.submissionBreakdown.series">
                        <highcharts :options="getBarChartOptions(chartsData.submissionBreakdown, false, true)" ></highcharts>
                    </div>
                    <!-- Application Spend -->
                    <div class="w-full mt-10" v-if="chartsData.applicationSpend.series">
                        <highcharts :options="getBarChartOptions(chartsData.applicationSpend, true)" ></highcharts>
                    </div>
                    <!-- Application Supplier Status -->
                    <div class="w-full mt-10" v-if="chartsData.applicationSupplierStatus.series">
                        <highcharts :options="getBarChartOptions(chartsData.applicationSupplierStatus)" ></highcharts>
                    </div>
                    <!-- Application Popularity By User -->
                    <div class="w-full mt-10" v-if="chartsData.applicationPopularityByUser.series">
                        <highcharts :options="getBarChartOptions(chartsData.applicationPopularityByUser)" ></highcharts>
                    </div>
                    <!-- Application Completion Efficiency -->
                    <div class="w-full mt-10" v-if="chartsData.applicationCompletionEfficiency.series">
                        <highcharts :options="getBarChartOptions(chartsData.applicationCompletionEfficiency)" ></highcharts>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import axios from 'axios'

// import Highcharts from 'highcharts-vue';
import Highcharts from 'highcharts'

import markerClustersInit from 'highcharts/modules/marker-clusters'
import colorAxisInit from 'highcharts/modules/coloraxis'
import proj4 from 'proj4'
import HighchartsMapModule from 'highcharts/modules/map'

markerClustersInit(Highcharts);
colorAxisInit(Highcharts);

window['proj4'] = proj4;

HighchartsMapModule(Highcharts);

// import mapData from '@highcharts/map-collection/countries/au/au-all.geo.json';


    export default {
        name: 'Dashboard',
        components: {
            // highcharts: Chart,
        },
        data() {
            return {
                chartsLoading: false,
                chartsColors: [
                    '#821d0c',
                    '#124d05',
                    '#b35c00',
                    '#3a005c',
                    '#005e80',
                    '#ffc83d',
                    '#61b045',
                    '#db3700'
                ],
                chartsData: {
                    totalUsers: {},
                    loggedMembers: {},
                    applicationPopularity: {},
                    applicationSpend: {},
                    applicationSupplierStatus: {},
                    applicationCompletionEfficiency: {},
                    applicationPopularityByUser: {},
                    applicationStatuses: [],
                    fasStatuses: [],
                    supplierStatuses: []
                },

            }
        },
        methods: {
            getData: function(){

                // this.$Progress.start();
                this.chartsLoading = true;

                axios.get(process.env.VUE_APP_API_URL + '/dashboards').then(response => {
                    if(response.data){
                        Object.entries(response.data).forEach(([key, value]) => {
                            this.chartsData[key] = value;
                        });
                    }
                    this.chartsLoading = false;
                    // this.$Progress.finsih();
                })
                .catch((err) => {
                    // this.$Progress.finsih();
                    this.chartsLoading = false;
                    console.log('dashboard error: ', err);
                });

            },
            statusCardClicked(cardType, statusName){ // cardTypes: application, tap, supplier
                let supplierStatusId = ''
                let statuses = []
                switch(cardType){
                    case 'application':
                        statuses = this.$store.getters.applicationStatuses
                        break;
                    case 'tap':
                        statuses = this.$store.getters.fasApplicationStatuses
                        break;
                    case 'supplier':
                        statuses = this.$store.getters.supplierStatuses
                        break;
                }
                let filterData = {}
                for(let x = 0; x < statuses.length; x++){
                    if(statuses[x]['name'] === statusName){
                        if(cardType === 'tap'){
                            filterData['fas_status'] = statuses[x]['id']
                        }else{
                            filterData[cardType + '_status'] = statuses[x]['id']
                        }
                        break;
                    }
                }
                this.$store.commit('SET_FILTERS', {
                    resource: 'submissions', data: filterData
                });
                this.$router.push('/submissions')
            },
            getStackedBarChartOptions: function(data){
                return {
                    chart: {
                        type: 'column'
                    },
                    title: {
                        text: data.title
                    },
                    xAxis: {
                        categories: data.categories
                    },
                    yAxis: {
                        min: 0,
                        title: {
                            enabled: false
                        },
                        stackLabels: {
                            enabled: true,
                            style: {
                                fontWeight: 'bold',
                                color: ( // theme
                                    Highcharts.defaultOptions.title.style &&
                                    Highcharts.defaultOptions.title.style.color
                                ) || 'gray'
                            }
                        }
                    },
                    legend: {
                        enabled: false
                    },
                    tooltip: {
                        headerFormat: '<b>{series.name}</b><br />',
                        pointFormat: '{point.y}'
                    },
                    plotOptions: {
                        column: {
                            stacking: 'normal',
                            dataLabels: {
                                format: '{series.name}: {point.y}',
                                enabled: true,
                                style: {
                                    fontSize: '11px',
                                    fontWeight: '400',
                                    textShadow: false,
                                    textOutline: '0.5px',
                                },
                            }
                        }
                    },
                    credits: {
                        enabled: false
                    },
                    series: data.series,
                    colors: this.monoChromaticColors('#0F487F', data.series.length, .3),
                };
            },
            getPieChartOptions: function(data){

                let chartOptions = {
                    chart: {
                        type: 'pie',
                        margin: [40, 10, 40, 10],
                        spacingTop: 0,
                        spacingBottom: 0,
                        spacingLeft: 0,
                        spacingRight: 0,
                        style: {
                            // fontFamily: 'Montserrat',
                        }
                    },
                    title: {
                        text: data.title
                    },
                    tooltip: {
                        pointFormat: '{series.name}: {point.y} <b>({point.percentage:.1f}%)</b>'
                    },
                    plotOptions: {
                        pie: {
                            size: '80%',
                            allowPointSelect: true,
                            cursor: 'pointer',
                            colors: this.monoChromaticColors('#0F487F', data.series.length, .2),
                            // colors: this.getChartColors(data.series.length),
                            dataLabels: {
                                enabled: true,
                                style: {
                                    fontSize: '13px',
                                    fontWeight: '400',
                                    textShadow: false,
                                    textOutline: '0.5px',
                                },
                                format: '',//format is defined later using conditions
                                // distance: data.labelDistance ? data.labelDistance : -50,
                                filter: {
                                    property: 'percentage',
                                    operator: '>',
                                    value: 1
                                },
                                alignTo: 'plotEdges',
                                connectorShape: 'crookedLine',
                                crookDistance: '80%',
                            }
                        }
                    },
                    series: [{
                        name: data.tooltipName,
                        data: data.series
                    }],
                    credits: {
                        enabled: false
                    },
                };

                // if (data.showActualNumber && data.showPercentage){
                //     chartOptions.tooltip.pointFormat = '{series.name}: {point.y} <b>({point.percentage:.1f}%)</b>' ;
                // } else if (data.showActualNumber){
                //     chartOptions.tooltip.pointFormat = '{series.name}: <b>{point.y}</b>';
                // } else{
                //     chartOptions.tooltip.pointFormat = '{series.name}: <b>{point.percentage:.1f}%</b>';
                // }

                if (data.showNumber && data.showPercentage){
                    chartOptions.plotOptions.pie.dataLabels.format = '{point.name}<br>{point.y} ({point.percentage:.1f}%)' ;
                } else if (data.showNumber){
                    chartOptions.plotOptions.pie.dataLabels.format = '{point.name}<br>{point.y}';
                } else{
                    chartOptions.plotOptions.pie.dataLabels.format = '{point.name}<br>{point.percentage:.1f} %';
                }

                return chartOptions;
            },
            getBarChartOptions: function(data, currency = false, percent = false){

                let headerFormat = '<span style="font-size:10px">{point.key}</span><table>'
                let pointFormat = '<tr><td style="color:{series.color};padding:0">{series.name}: </td><td style="padding:0"><b>{point.y}</b></td></tr>'

                if (currency){
                    headerFormat = '<span style="font-size:10px">{point.key}</span><table>'
                    pointFormat = '<tr><td style="color:{series.color};padding:0">{series.name}: </td><td style="padding:0"><b>${point.y}</b></td></tr>'
                }

                if (percent){
                    headerFormat = '<span style="font-size:10px">{point.key}</span><table>'
                    pointFormat = '<tr><td style="color:{series.color};padding:0">{series.name}: </td><td style="padding:0"><b>{point.y}%</b></td></tr>'
                }

                let chartOptions = {
                    chart: {
                        type: 'column',
                        backgroundColor: 'transparent'
                    },
                    title: {
                        text: data.title
                    },
                    xAxis: {
                        categories: data.xAxisCategories,
                        crosshair: true
                    },
                    yAxis: {
                        min: 0,
                        title: {
                            text: data.yAxisLabel,
                        }
                    },
                    tooltip: {
                        headerFormat: headerFormat,
                        pointFormat: pointFormat,
                        footerFormat: '</table>',
                        shared: true,
                        useHTML: true
                    },
                    plotOptions: {
                        column: {
                            pointPadding: 0.2,
                            borderWidth: 0,
                        }
                    },
                    // colors: this.monoChromaticColors('#821d0c', data.series.length, 1.5),
                    colors: this.getChartColors(data.series.length),
                    series: data.series,
                    credits: {
                        enabled: false
                    },
                };

                return chartOptions;
            },
            monoChromaticColors: function (baseColor, noOfColors, step) {
                let base = baseColor;
                let colors = [base];

                for (let i = 1; i < noOfColors; i += 1) {

                    let lighterColor = this.ColorLuminance(base, step);
                    colors.push(lighterColor);
                    base = lighterColor;
                }
                return colors;
            },
            getChartColors: function (length) {
                let colors = [];
                for (let i = 0; i < length; i += 1){
                    colors.push(this.chartsColors[i]);
                }
                return colors;
            },
            ColorLuminance: function(hex, lum) {

                // validate hex string
                hex = String(hex).replace(/[^0-9a-f]/gi, '');
                if (hex.length < 6) {
                    hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
                }
                lum = lum || 0;

                // convert to decimal and change luminosity
                var rgb = "#", c, i;
                for (i = 0; i < 3; i++) {
                    c = parseInt(hex.substr(i*2,2), 16);
                    c = Math.round(Math.min(Math.max(0, c + (c * lum)), 255)).toString(16);
                    rgb += ("00"+c).substr(c.length);
                }

                return rgb;
            }
        },
        beforeMount() {

        },
        mounted() {
            this.getData();
        },
        computed: {

        }
    }
</script>

<style scoped>
.highcharts-credits{
    display: none;
}
</style>
